import React, { useEffect, useMemo, useRef, useState } from 'react';
import Dropdown from 'react-bootstrap/Dropdown';
import { useTranslation } from 'react-i18next';
import styles from './ProjectSearchMenu.module.css';

export interface ProjectOption {
  id: string;
  name: string;
}

interface ProjectSearchMenuProps {
  isOpen: boolean;
  onSelect: (project: ProjectOption) => void;
  projects: ProjectOption[];
}

const ProjectSearchMenu = ({
  isOpen,
  onSelect,
  projects,
}: ProjectSearchMenuProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [openSnapshot, setOpenSnapshot] = useState(isOpen);

  if (isOpen !== openSnapshot) {
    setOpenSnapshot(isOpen);
    if (!isOpen) {
      setQuery('');
      setHighlightedIndex(0);
    }
  }

  const filteredProjects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return projects
      .filter(project => (
        !normalizedQuery || project.name.toLowerCase().includes(normalizedQuery)
      ))
      .sort((a, b) => {
        if (!a.id) {
          return b.id ? -1 : 0;
        }
        if (!b.id) {
          return 1;
        }
        return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      });
  }, [projects, query]);
  const activeIndex = filteredProjects.length === 0
    ? 0
    : Math.min(highlightedIndex, filteredProjects.length - 1);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const item = listRef.current?.querySelectorAll<HTMLElement>('.dropdown-item')[activeIndex];
    if (typeof item?.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, filteredProjects, isOpen]);

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      return;
    }
    event.stopPropagation();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (filteredProjects.length === 0) {
        return;
      }
      setHighlightedIndex(Math.min(activeIndex + 1, filteredProjects.length - 1));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(Math.max(activeIndex - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const match = filteredProjects[activeIndex] ?? filteredProjects[0];
      if (match) {
        onSelect(match);
      }
    }
  };

  return (
    <>
      <input
        aria-label={t('search')}
        autoComplete="off"
        className={styles.search}
        onChange={event => {
          setQuery(event.target.value);
          setHighlightedIndex(0);
        }}
        onClick={event => event.stopPropagation()}
        onKeyDown={handleSearchKeyDown}
        placeholder={t('search')}
        ref={inputRef}
        value={query}
      />
      <div className={styles.list} ref={listRef}>
        {
          filteredProjects.map((project, index) => (
            <Dropdown.Item
              active={index === activeIndex}
              key={project.id || project.name}
              onClick={() => onSelect(project)}
              title={project.name}
            >
              {project.name}
            </Dropdown.Item>
          ))
        }
      </div>
    </>
  );
};

export default ProjectSearchMenu;
